import type { ExtractedSpreadsheetItem } from './purchaseSpreadsheet';

/**
 * Classify a normalised header string to a canonical field name.
 * Exported so that UI code and tests can call the same logic as the parser.
 * @param rawHeader - raw cell text (NOT yet normalised)
 * @returns canonical field name, or null if unrecognised
 */
export function classifyHeuristicColumn(rawHeader: string): string | null {
  if (!rawHeader || typeof rawHeader !== 'string') return null;
  const norm = rawHeader.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (norm === 'notes' || norm === 'note') return 'notes';
  if (norm === 'public' || norm === 'ispublic') return 'is_public';
  if (norm === 'brand' || norm === 'make' || norm.includes('brand')) return 'brand';
  if (norm === 'model' || norm.includes('model')) return 'model';
  if (norm === 'sn' || norm.includes('serial')) return 'serial_number';
  if (norm.includes('tag') || norm.includes('servicetag')) return 'service_tag';
  if (norm.includes('barcode')) return 'barcode';
  if (norm.includes('cpu') || norm.includes('processor')) return 'cpu';
  if (norm.includes('ram') || norm.includes('memory')) return 'ram';
  if (norm.includes('nvme') || norm.includes('ssd') || norm.includes('hdd') || norm.includes('storage') || norm.includes('disk')) return 'storage';
  if (norm.includes('gpu') || norm.includes('graphic')) return 'gpu';
  // Cost fields — specific BEFORE broad
  if (norm.includes('refurb')) return 'refurbishment_cost';
  if (norm.startsWith('other') && norm.includes('cost')) return 'other_cost';
  if (norm.includes('unitcost') || norm.includes('purchasecost') || norm.includes('purchaseprice') || norm.includes('unitprice')) return 'unit_cost';
  if (norm.includes('cost') || norm.includes('rate')) return 'unit_cost';
  if (norm.includes('sale') || norm.includes('selling') || norm.includes('asking') || norm.includes('retail')) return 'selling_price';
  if (norm.includes('minimum') || norm.includes('minprice') || norm.includes('floor')) return 'minimum_price';
  // Touchscreen — exact BEFORE generic 'screen'
  if (norm === 'touchscreen' || norm === 'istouch' || norm === 'touch' || norm === 'hastouchscreen') return 'touchscreen';
  if (norm.includes('resolution')) return 'resolution';
  if (norm.includes('screen') || norm.includes('display') || norm.includes('screensize')) return 'screen_size';
  // Charger fields — specific BEFORE broad
  if (norm.includes('chargernote') || norm.includes('chargerdesc') || norm.includes('chargerdescription')) return 'charger_notes';
  if (norm === 'chargerincluded' || norm === 'hascharger' || norm === 'charger') return 'charger_included';
  if (norm.includes('charger')) return 'charger_included';
  if (norm.includes('condition')) return 'condition';
  if (norm.includes('grade')) return 'grade';
  if (norm.includes('battery')) return 'battery_health';
  if (norm.includes('colour') || norm.includes('color')) return 'colour';
  if (norm.includes('keyboard')) return 'keyboard_layout';
  if (norm.includes('os') || norm.includes('operating')) return 'operating_system';
  if (norm.includes('warranty')) return 'warranty_months';
  if (norm.includes('accessor')) return 'accessories_included';
  if (norm.includes('desc') || norm.includes('spec') || norm.includes('item')) return 'description';
  if (norm.includes('qty') || norm.includes('quantity')) return 'quantity';
  return null;
}

/**
 * Laptop Quantity Expander (Expands Quantity N into N Physical Units)
 */
export function expandLaptopUnits(
  item: ExtractedSpreadsheetItem,
  quantity: number
): ExtractedSpreadsheetItem[] {
  const count = Number(quantity);
  if (!Number.isSafeInteger(count) || count < 1 || count > 2000) {
    throw new Error('Laptop quantity must be a whole number from 1 to 2000.');
  }
  if (count === 1) {
    return [{ ...item, quantity: 1 }];
  }

  const result: ExtractedSpreadsheetItem[] = [];
  for (let i = 1; i <= count; i++) {
    const unitWarnings = [...(item.warnings || [])];
    let unitSerial = item.serial_number;
    let unitTag = item.service_tag;
    let unitBarcode = item.barcode;

    if (i > 1) {
      // Do NOT clone serials across multiple units
      unitSerial = undefined;
      unitTag = undefined;
      unitBarcode = undefined;
      unitWarnings.push(`Unit ${i} of ${count} expanded from batch quantity — review identifiers before receiving`);
    }

    result.push({
      ...item,
      id: `${item.id}-u${i}`,
      serial_number: unitSerial,
      service_tag: unitTag,
      barcode: unitBarcode,
      quantity: 1,
      warnings: unitWarnings,
      is_valid: item.is_valid && unitWarnings.length === 0,
    });
  }

  return result;
}

/**
 * Laptop Group Key Computation for Receiving Batches
 */
export function computeLaptopGroupKey(item: Partial<ExtractedSpreadsheetItem>): string {
  return [
    item.brand || '',
    item.model || '',
    item.cpu || '',
    item.ram || '',
    item.storage || '',
    item.gpu || '',
    item.screen_size || '',
    String(item.touchscreen ?? ''),
    item.resolution || '',
    item.colour || '',
    item.keyboard_layout || '',
    item.operating_system || '',
    item.condition || 'good',
    item.grade || '',
    String(item.battery_health ?? ''),
    String(item.charger_included ?? ''),
    item.charger_notes || '',
    item.accessories_included || '',
    String(item.unit_cost ?? 0),
    String(item.refurbishment_cost ?? 0),
    String(item.other_cost ?? 0),
    String(item.selling_price ?? 0),
    String(item.minimum_price ?? ''),
    String(item.warranty_months ?? ''),
    item.description || '',
    item.notes || '',
    String(item.is_public ?? true),
  ].join('||');
}
